import { formatTarget, type ClientTarget } from '@features/clients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getDonationsReconciliation, requestDonationsReconciliation } from '../api/bulk-migrations';

import {
    afterRequestFailed,
    afterRequestSucceeded,
    reconciliationKey,
    reconciliationRefetchInterval,
    reconciliationRetry,
} from './reconciliation-query';

export { readFailureOf, reconciliationKey } from './reconciliation-query';

/**
 * Server state for the donations reconciliation panel (INI-0034 S7). The policy
 * (key, retry, poll, what a request does to the cache) lives in
 * `reconciliation-query.ts`, where it is tested; these hooks only wire it.
 */

/**
 * The latest reconciliation of one client, re-read every 5 s while it is requested
 * or running, and never after a 4xx. A 4xx (module off, none yet, bad id) is an
 * answer, never retried.
 */
export function useDonationsReconciliation(target: ClientTarget, tenantId: string | null) {
    const id = formatTarget(target);
    return useQuery({
        queryKey: reconciliationKey(id ?? 'unaddressable', tenantId ?? ''),
        queryFn: ({ signal }) => getDonationsReconciliation(target, tenantId ?? '', signal),
        enabled: id !== null && tenantId !== null,
        retry: reconciliationRetry,
        refetchInterval: (query) =>
            reconciliationRefetchInterval(
                { data: query.state.data, error: query.state.error },
                Date.now(),
            ),
    });
}

/**
 * Ask for a new one. Never retried by the client: a second request is the
 * operator's decision. On success the request becomes the cached latest, then the
 * poll takes over; on a 409 (one already running) the latest is re-read.
 */
export function useRequestDonationsReconciliation(target: ClientTarget, tenantId: string | null) {
    const queryClient = useQueryClient();
    const key = reconciliationKey(formatTarget(target) ?? 'unaddressable', tenantId ?? '');
    return useMutation({
        mutationFn: () => requestDonationsReconciliation(target, tenantId ?? ''),
        onSuccess: (document) => {
            afterRequestSucceeded(queryClient, key, document);
        },
        onError: () => afterRequestFailed(queryClient, key),
        retry: false,
        networkMode: 'always',
    });
}
